import { JSX, useEffect, useRef, useState } from 'react';
import DateTitleBar from './dateTitleBar';
import { CalendarEvent, CalendarId, calendarService } from '../../services/calendarService';
import EventView from '../../add-page/events/eventView'
import Loading from '../../common/Loading/loading';
import './calendarView.css';

interface CalendarProps {
    initialDate?: Date;
    onDateSelect?: (date: Date) => void;
}

const CalendarView = ({ 
    initialDate = new Date(),
    onDateSelect
}: CalendarProps ) : JSX.Element => {
    const [currentMonth, setCurrentMonth] = useState(new Date(initialDate));
    const [selectedDate, setSelectedDate] = useState(new Date(initialDate));
    const [calendarDays, setCalendarDays] = useState<Array<{ date: Date | null, isCurrentMonth: boolean }>>([]);
    const [events, setEvents] = useState<CalendarEvent[]>([]);
    const [filteredEvents, setFilteredEvents] = useState<CalendarEvent[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [transitionDirection, setTransitionDirection] = useState('');

    const calendarRef = useRef(null);
    const touchStartX = useRef(0);
    
    const getDisplayDateRange = (): string => {
        const month = selectedDate.toLocaleString('default', { month: 'long'});
        const year = selectedDate.getFullYear();
        return `${month} ${year}`;
    }

    useEffect(() => {
        const days = generateCalendarDays(currentMonth);
        setCalendarDays(days);
        fetchEvents(true);
    }, [currentMonth]);

    const fetchEvents = async (showLoading: boolean = false) => {
        if (showLoading) {
            setIsLoading(true);
        }
        const startDate = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
        const endDate = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0);
        const events = await calendarService.getEvents(startDate.toISOString(), endDate.toISOString());
        setEvents(events);
        if (showLoading) {
            setIsLoading(false);
        }
    }

    const generateCalendarDays = (date: Date) => {
        const year = date.getFullYear();
        const month = date.getMonth();
        
        // First day of the month
        const firstDayOfMonth = new Date(year, month, 1);
        // Last day of the month
        const lastDayOfMonth = new Date(year, month + 1, 0);
        
        // Day of the week for the first day (0 = Sunday, 6 = Saturday)
        const firstDayOfWeek = firstDayOfMonth.getDay();
        // Total days in the month
        const daysInMonth = lastDayOfMonth.getDate();
        
        // Array to hold all calendar days, including those from previous/next months
        const calendarDays = [];
        
        // Add days from previous month to fill the first row
        const prevMonth = new Date(year, month, 0);
        const daysInPrevMonth = prevMonth.getDate();
        
        for (let i = 0; i < firstDayOfWeek; i++) {
          const day = daysInPrevMonth - firstDayOfWeek + i + 1;
          calendarDays.push({
            date: new Date(year, month - 1, day),
            isCurrentMonth: false
          });
        }
        
        // Add days from current month
        for (let day = 1; day <= daysInMonth; day++) {
          calendarDays.push({
            date: new Date(year, month, day),
            isCurrentMonth: true
          });
        }
        
        // Add days from next month to complete the grid (we'll always have 6 rows)
        const remainingDays = 42 - calendarDays.length; // 6 rows * 7 days = 42
        for (let day = 1; day <= remainingDays; day++) {
          calendarDays.push({
            date: new Date(year, month + 1, day),
            isCurrentMonth: false
          });
        }
        
        return calendarDays;
      };
      
      // Handle date click
      const handleDateClick = (day: { date: Date | null, isCurrentMonth: boolean }) => {
        if (day.date) {
          setSelectedDate(day.date);
          if (onDateSelect) onDateSelect(day.date);
        }
      };
      
      // Navigate to previous month
      const prevMonth = () => {
        if (isTransitioning) return;
    
        setTransitionDirection('right');
        setIsTransitioning(true);
        
        setTimeout(() => {
        setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
        setSelectedDate(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
        setIsTransitioning(false);
        }, 300);
      };
      
      // Navigate to next month
      const nextMonth = () => {
        if (isTransitioning) return;
    
        setTransitionDirection('left');
        setIsTransitioning(true);
        
        setTimeout(() => {
        setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
        setSelectedDate(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
        setIsTransitioning(false);
        }, 300);
      };

      const handleTouchStart = (e) => {
        touchStartX.current = e.touches[0].clientX;
      };

      const handleTouchEnd = (e) => {
        const touchEndX = e.changedTouches[0].clientX;
        const difference = touchStartX.current - touchEndX;
        
        // If swipe distance is significant enough (e.g., > 50px)
        if (Math.abs(difference) > 50) {
          if (difference > 0) {
            // Swipe left, go to next month
            nextMonth();
          } else {
            // Swipe right, go to previous month
            prevMonth();
          }
        }
      };
      
      // Scroll wheel navigation
      const handleWheel = (e) => {
        // Adding a small debounce/throttle effect
        if (isTransitioning) return;
        
        // Horizontal scroll for trackpads
        if (Math.abs(e.deltaX) > Math.abs(e.deltaY) && Math.abs(e.deltaX) > 30) {
          if (e.deltaX > 0) {
            nextMonth();
          } else {
            prevMonth();
          }
          return;
        }
        
        // Vertical scroll
        if (Math.abs(e.deltaY) > 30) {
          if (e.deltaY > 0) {
            nextMonth();
          } else {
            prevMonth();
          }
        }
      };
      
      // Format month name
      const formatMonth = (date: Date) => {
        return date.toLocaleString('default', { month: 'long', year: 'numeric' });
      };
      
      // Check if date is today
      const isToday = (date: Date) => {
        const today = new Date();
        return date.getDate() === today.getDate() &&
          date.getMonth() === today.getMonth() &&
          date.getFullYear() === today.getFullYear();
      };
      
      // Check if date is selected
      const isSelected = (date: Date) => {
        return date.getDate() === selectedDate.getDate() &&
          date.getMonth() === selectedDate.getMonth() &&
          date.getFullYear() === selectedDate.getFullYear();
      };

    const getEventCalendarIds = (date: Date) => {
        // Create an array to store matching calendar IDs
        const matchingCalendarIds: CalendarId[] = [];
        
        // Loop through all events
        events.forEach((event) => {
            const eventTime = new Date(event.start.date || event.start.dateTime);
            
            // Check if the date matches (year, month, day)
            if (date.getFullYear() === eventTime.getFullYear() && 
                date.getMonth() === eventTime.getMonth() && 
                date.getDate() === eventTime.getDate()) {
                
                // Add the calendar ID to our array
                if (event.calendarId) {
                    matchingCalendarIds.push(event.calendarId);
                }
            }
        });
        
        return matchingCalendarIds;
    };

    const getCalendarColor = (calendarId: CalendarId) => {
        // Map calendar IDs to colors
        const calendarColors: Record<CalendarId, string> = {
            'work': '#4285F4',       // Blue
            'primary': '#0F9D58',   // Green
            'us holiday': '#394047',
            'taiwan holiday': '#DB4437',    // Red
            'HYTE': '#F4B400',  // Yellow
            // Add more as needed
        };
        
        return calendarColors[calendarId] || '#9E9E9E'; // Default gray if not found
    };

    useEffect(() => {
        const filtered = events.filter(event => {
            // Get the event's date (from either date or dateTime property)
            const eventTime = new Date(event.start.date || event.start.dateTime);
            
            // Check if year, month, and day match the selected date
            return (
                eventTime.getFullYear() === selectedDate.getFullYear() &&
                eventTime.getMonth() === selectedDate.getMonth() &&
                eventTime.getDate() === selectedDate.getDate()
            );
        });
        
        setFilteredEvents(filtered);
    }, [selectedDate, events])

    return (
        <div>
            <div className='calendar-date-view'>
                <p onClick={prevMonth} className='display-date-icon'>{"<"}</p>
                <p>{getDisplayDateRange()}</p>
                <p onClick={nextMonth} className='display-date-icon'>{">"}</p>
            </div>
            <div className='divider-horizontal'/>
            <DateTitleBar/>
            <div className={`calendar-grid-container ${isTransitioning ? 'transitioning' : ''} ${transitionDirection}`}
            ref={calendarRef}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            onWheel={handleWheel}>
                <div className="calendar-grid">
                    {calendarDays.map((day, index) => (
                    <div 
                        key={index}
                        className={`calendar-day ${!day.isCurrentMonth ? 'other-month' : ''} ${day.date && isToday(day.date) ? 'today' : ''} ${day.date && isSelected(day.date) ? 'selected' : ''}`}
                        onClick={() => day.date && handleDateClick(day)}
                    >
                        <div className="date-container">
                            <span className="date-number">{day.date && day.date.getDate()}</span>
                            {day.date && (
                            <div className='event-indicator-container'>
                                {getEventCalendarIds(day.date).map((calendarId, idx) => (
                                    <div 
                                        key={idx}
                                        className="event-indicator"
                                        style={{ backgroundColor: getCalendarColor(calendarId) }}
                                    ></div>
                                ))}
                            </div>
                        )}
                        </div>
                    </div>
                    ))}
                </div>
            </div>
            <div className='divider-horizontal'/>
            <div className='events-view-container'>
                {filteredEvents.length > 0 ? (
                    filteredEvents.map((event) => (
                        <EventView key={event.id} event={event} displayDate={true} handelEventUpdate={fetchEvents}/>
                    ))
                ) : (
                    <div className="no-events-message">No events found</div>
                )}
            </div>
            {isLoading && <Loading/>}
        </div>
    )
}

export default CalendarView;